import { useCallback, useEffect, useRef, useState } from "react";
import { useResizeDetector } from "react-resize-detector";
import {
  Measurement,
  TagMetaData,
} from "src/config/store/services/media-api/types";
import { TagStyles } from "src/modules/shared/components/media-player/video-tag/video-tag";
import { useGlobalEvent } from "../../hooks/use-global-event";
import { MediaType } from "./media-player";
import { useTagsDrawer } from "./tags-drawer/use-tags-drawer";
import { ControlsMethods } from "./video-controls/video-controls";
import screenfull from "screenfull";

const MIN_SIZE = 50;
// const MIN_LABEL_SCALE = 3;

type TagPosition = {
  yPosition: number;
  xPosition: number;
  isVisible: boolean;
  size: number;
};

export function isVideoType(
  value: HTMLVideoElement | HTMLImageElement
): value is HTMLVideoElement {
  return (value as HTMLVideoElement).play !== undefined;
}

const normalizeSize = (size: number) => {
  return size <= MIN_SIZE ? MIN_SIZE : size;
};

// const normalizeLabelScale = (scale: number) => {
//   return scale <= 1 ? MIN_LABEL_SCALE : scale;
// };

export const getTagPosition = (
  media: HTMLVideoElement | HTMLImageElement,
  measure: Measurement
): TagPosition => {
  const size = measure.height * media.offsetHeight;

  const xPosition = measure.x * media.offsetWidth + media.offsetLeft;
  const yPosition = measure.y * media.offsetHeight;

  const isVisible =
    xPosition > media.offsetLeft &&
    xPosition < media.offsetLeft + media.offsetWidth &&
    yPosition > media.offsetTop &&
    yPosition < media.offsetTop + media.offsetHeight;

  return {
    size: normalizeSize(size),
    yPosition,
    xPosition,
    isVisible,
  };
};

let controlsTimer: NodeJS.Timeout | null = null;

let interval: NodeJS.Timeout;

export const useMediaPlayer = (tagsData: TagMetaData[], type: MediaType) => {
  const { drawerOpen, toggleDrawer } = useTagsDrawer();

  const mediaRef = useRef<HTMLVideoElement | HTMLImageElement>(null);

  const videoControls = useRef<ControlsMethods>(null);

  const tagRefs = useRef([] as HTMLDivElement[]);

  const { height, width, ref } = useResizeDetector();

  const [isPlaying, setIsPlaying] = useState(false);
  const [showTags, setShowTags] = useState(true);

  const tagsToggled = useRef(false);

  const [metaDataLoaded, setMetadataLoaded] = useState<boolean>(false);

  const [showControls, setShowControls] = useState(true);

  const setTagRefs = (node: HTMLDivElement, index: number) => {
    tagRefs.current[index] = node;
  };

  const videoOrientation = useCallback(() => {
    if (mediaRef.current && isVideoType(mediaRef.current)) {
      return mediaRef.current.videoWidth > mediaRef.current.videoHeight
        ? "horizontal"
        : "vertical";
    }

    return "horizontal";
  }, []);

  const handleTagPositionNormalized = useCallback(
    (tag: TagMetaData, milliseconds: number) => {
      let measure = tag.nodeMeasures.find((measure) => {
        return milliseconds <= measure.time;
      });

      if (mediaRef.current && measure) {
        const { yPosition, xPosition, isVisible, size } = getTagPosition(
          mediaRef.current,
          measure
        );

        const style: TagStyles = {
          top: `${yPosition}px`,
          left: `${xPosition}px`,
          display: isVisible ? "flex" : "none",
          width: `${size}px`,
          height: `${size}px`,
        };

        const tagRef = tagRefs.current.find((ref) => ref?.id === tag.id);

        if (tagRef) {
          console.log(tagRef.children[1]);
          (Object.keys(style) as Array<keyof TagStyles>).forEach((prop) => {
            tagRef.style[prop] = style[prop];
          });

          // const tagLabel = tagRef.children[1] as HTMLDivElement;

          // tagLabel.style.transform = `scale(${normalizeLabelScale(
          //   transformScale
          // )})`;
        }
      }
    },
    []
  );

  const handleTags = useCallback(() => {
    const currentTime =
      (mediaRef?.current as HTMLVideoElement)?.currentTime || 0;

    const milliseconds = currentTime * 1000;

    tagsData.forEach((tag) => {
      handleTagPositionNormalized(tag, milliseconds);
    });
  }, [handleTagPositionNormalized, tagsData]);

  const watchForTimeChange = useCallback(() => {
    if (showTags) {
      handleTags();
    }

    if (showControls) {
      videoControls?.current?.handleOnTimeUpdate();
    }
  }, [handleTags, showControls, showTags]);

  const togglePlay = useCallback(() => {
    setIsPlaying(!isPlaying);

    if (isPlaying) {
      setShowControls(true);
      // !tagsToggled.current && setShowTags(true);
    }

    // if (!isPlaying) {
    //   !tagsToggled.current && setShowTags(false);
    // }
  }, [isPlaying, setIsPlaying]);

  const toggleTags = useCallback(() => {
    tagsToggled.current = true;

    setShowTags(!showTags);
  }, [showTags, setShowTags]);

  const toggleFullScreen = useCallback(async () => {
    if (drawerOpen) {
      toggleDrawer();
    }

    if (screenfull.isEnabled) {
      screenfull.toggle(ref.current);

      if (!screenfull.isFullscreen && videoOrientation() === "horizontal") {
        try {
          await window.screen.orientation.lock("landscape");
        } catch (error) {}
      }
    }
  }, [ref, drawerOpen, toggleDrawer, videoOrientation]);

  const handleShowControls = (showControl: boolean) => {
    setShowControls(showControl);
    clearTimeout(controlsTimer as NodeJS.Timeout);

    if (showControl) {
      controlsTimer = setTimeout(() => {
        setShowControls(false);
      }, 2000);
    }
  };

  useEffect(() => {
    return () => {
      if (controlsTimer) {
        clearTimeout(controlsTimer);
      }
    };
  }, []);

  useEffect(() => {
    if (mediaRef.current && isVideoType(mediaRef?.current)) {
      isPlaying ? mediaRef.current.play() : mediaRef.current?.pause();
      interval = setInterval(() => watchForTimeChange(), 100);

      if (!isPlaying) {
        clearInterval(interval);
      }
    }

    return () => {
      clearInterval(interval);
    };
  }, [isPlaying, watchForTimeChange]);

  useEffect(() => {
    if (metaDataLoaded && showTags) {
      handleTags();
    }
  }, [height, width, handleTags, isPlaying, metaDataLoaded, showTags]);

  const handleSpacePress = useCallback(
    (e: KeyboardEvent) => {
      if (e.code === "Space") {
        e.preventDefault();
        togglePlay();
      }
    },
    [togglePlay]
  );

  const handleArrowsPress = (e: KeyboardEvent) => {
    if (mediaRef?.current && isVideoType(mediaRef?.current)) {
      switch (e.code) {
        case "ArrowRight":
          mediaRef.current.currentTime = mediaRef.current.currentTime + 5;
          watchForTimeChange();
          break;
        case "ArrowLeft":
          mediaRef.current.currentTime = mediaRef.current.currentTime - 5;
          watchForTimeChange();
          break;
      }
    }
  };

  useGlobalEvent(handleSpacePress, "keyup", type === "photo");
  useGlobalEvent(handleArrowsPress, "keydown", type === "photo");

  return {
    mediaRef,
    videoControls,
    tagRefs,
    wrapperRef: ref,
    isPlaying,
    metaDataLoaded,
    drawerOpen,
    showTags,
    showControls,
    videoOrientation,
    setIsPlaying,
    setMetadataLoaded,
    handleTags,
    togglePlay,
    handleSpacePress,
    handleArrowsPress,
    toggleFullScreen,
    toggleDrawer,
    toggleTags,
    handleShowControls,
    setTagRefs,
  };
};
